import clsx from "clsx";
import { FC, ReactNode } from "react";

import type { ContentDocument } from "@tsmono/inspect-common/types";
import { RequireMedia } from "@tsmono/react/components";
import { Icon, iconForMimeType } from "@tsmono/react/icons";
import { isImage } from "@tsmono/util";

import { MediaReference } from "../../media/MediaReference";
import { isRenderableImageDocument } from "../../media/mediaSource";

import styles from "./ContentDocumentView.module.css";

interface ContentDocumentProps {
  id: string;
  document: ContentDocument;
  onDownloadFile?: (filename: string, document: string) => void;
}

export const ContentDocumentView: FC<ContentDocumentProps> = ({
  id,
  document,
  onDownloadFile,
}) => {
  if (isImage(document.mime_type || "")) {
    const preview = isRenderableImageDocument(
      document.document,
      document.mime_type || ""
    ) ? (
      <RequireMedia kind="image">
        <img
          className={clsx(styles.imageDocument)}
          src={document.document}
          alt={document.filename}
          id={id}
        />
      </RequireMedia>
    ) : (
      <MediaReference source={document.document} />
    );

    return (
      <ContentDocumentFrame document={document} onDownloadFile={onDownloadFile}>
        {preview}
      </ContentDocumentFrame>
    );
  } else {
    return (
      <ContentDocumentFrame
        document={document}
        onDownloadFile={onDownloadFile}
      />
    );
  }
};

interface ContentDocumentFrameProps {
  children?: ReactNode;
  document: ContentDocument;
  onDownloadFile?: (filename: string, document: string) => void;
}

const ContentDocumentFrame: FC<ContentDocumentFrameProps> = ({
  document,
  children,
  onDownloadFile,
}) => {
  return (
    <div
      className={clsx(
        styles.documentFrame,
        "text-size-small",
        "text-style-secondary"
      )}
    >
      <div className={clsx(styles.documentFrameTitle)}>
        <Icon name={iconForMimeType(document.mime_type || "")} />
        <div>
          {onDownloadFile ? (
            <button
              type="button"
              className={clsx(styles.downloadLink)}
              onClick={() => {
                onDownloadFile(document.filename, document.document);
              }}
            >
              {document.filename}
            </button>
          ) : (
            document.filename
          )}
        </div>
      </div>
      {children}
    </div>
  );
};
